import React, { useEffect, useState } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import { ThreeCircles } from 'react-loader-spinner';
import css from './ReportCardLibrary.module.css';
import ReportCardPreviewModal from './ReportCardPreviewModal';

const TERM_ORDER = { first: 1, second: 2, third: 3 };

function termLabel(term) {
  return term === 'first' ? 'First' : term === 'second' ? 'Second' : term === 'third' ? 'Third' : term;
}

export default function ReportCardLibrary({
  fetchFn,
  listPath,
  downloadPath,
  filenamePrefix = 'report-card',
  title = 'Released Report Cards',
  subtitle = 'Official term reports published by the school.',
  emptyTitle = 'No released report cards yet',
  emptyDescription = 'Published term reports will appear here automatically.',
  titleId = 'released-report-cards-title',
}) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('newest');
  const [downloading, setDownloading] = useState('');
  const [previewing, setPreviewing] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchFn(listPath)
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to load released report cards.');
        if (active) setCards(data.reportCards || []);
      })
      .catch(err => { if (active) setError(err.message || 'Connection error.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fetchFn, listPath]);

  const sortedCards = [...cards].sort((left, right) => {
    const sessionCompare = String(left.session).localeCompare(String(right.session), undefined, { numeric: true });
    const termCompare = (TERM_ORDER[left.term] || 0) - (TERM_ORDER[right.term] || 0);
    if (sort === 'oldest') return sessionCompare || termCompare;
    if (sort === 'term') return termCompare || sessionCompare;
    return -(sessionCompare || termCompare);
  });

  const download = async card => {
    const key = `${card.term}||${card.session}`;
    setDownloading(key);
    setError('');
    try {
      const response = await fetchFn(downloadPath(card));
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to download report card.');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filenamePrefix}-${card.term}-${String(card.session).replace('/', '-')}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Connection error while downloading.');
    } finally {
      setDownloading('');
    }
  };

  const previewCard = async card => {
    const key = `${card.term}||${card.session}`;
    setPreviewing(key);
    setError('');
    try {
      const response = await fetchFn(downloadPath(card));
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to prepare report card preview.');
      }
      const blob = await response.blob();
      setPreview({
        url: URL.createObjectURL(blob),
        title: `${termLabel(card.term)} Term Report${card.session ? ` · ${card.session}` : ''}`,
        filename: `${filenamePrefix}-${card.term}-${String(card.session).replace('/', '-')}.pdf`,
      });
    } catch (err) {
      setError(err.message || 'Connection error while preparing preview.');
    } finally {
      setPreviewing('');
    }
  };

  return (
    <section className={css.library} aria-labelledby={titleId}>
      <div className={css.header}>
        <div>
          <h3 id={titleId} className={css.title}>{title}</h3>
          <p className={css.subtitle}>{subtitle}</p>
        </div>
        {cards.length > 1 && (
          <label className={css.sortControl}>
            <SortIcon />
            <span>Sort</span>
            <select value={sort} onChange={event => setSort(event.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="term">By term</option>
            </select>
          </label>
        )}
      </div>

      {error && <div className={css.error}>{error}</div>}

      {loading ? (
        <div className={css.loading}><ThreeCircles height="28" width="28" color="#3e8f93" visible /><span>Checking released reports…</span></div>
      ) : cards.length === 0 ? (
        <div className={css.empty}>
          <DescriptionIcon />
          <div>
            <strong>{emptyTitle}</strong>
            <span>{emptyDescription}</span>
          </div>
        </div>
      ) : (
        <div className={css.grid}>
          {sortedCards.map(card => {
            const key = `${card.term}||${card.session}`;
            const busy = downloading === key;
            return (
              <article key={key} className={css.reportCard}>
                <div className={css.documentIcon}><DescriptionIcon /></div>
                <div className={css.reportInfo}>
                  <strong>{termLabel(card.term)} Term</strong>
                  <span>{card.session}</span>
                  <small>{card.assessmentCount} assessment{card.assessmentCount !== 1 ? 's' : ''} included</small>
                </div>
                <div className={css.actionGroup}>
                  <button
                    type="button"
                    className={css.previewButton}
                    onClick={() => previewCard(card)}
                    disabled={!!downloading || !!previewing}
                    title="Preview report card"
                    aria-label={`Preview ${termLabel(card.term)} term report`}
                  >
                    {previewing === key ? <ThreeCircles height="16" width="16" color="#1a6e73" visible /> : <VisibilityIcon />}
                    <span>Preview</span>
                  </button>
                  <button type="button" className={css.downloadButton} onClick={() => download(card)} disabled={!!downloading || !!previewing}>
                    {busy ? <ThreeCircles height="16" width="16" color="#fff" visible /> : <DownloadIcon />}
                    <span>{busy ? 'Preparing' : 'PDF'}</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      <ReportCardPreviewModal
        url={preview?.url}
        title={preview?.title}
        downloadName={preview?.filename}
        onClose={() => setPreview(null)}
      />
    </section>
  );
}